import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container, Paper, Typography, Button, Box, Grid, Card,
  CardActionArea, CircularProgress, Divider, Chip,
  Breadcrumbs, Link as MuiLink, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  ArrowForward, ArrowBack, VerifiedUser,
  ExpandMore, AccessTime, AssignmentOutlined,
  NavigateNext as NavigateNextIcon, CheckCircle
} from '@mui/icons-material';
import { getServiceDetailData, ServiceDetailData } from '../data/servicesData';
import ServiceContactForm from '../components/ServiceContactForm';
import SEOHead from '../components/SEOHead';
import { Helmet } from 'react-helmet-async';

const ServiceDetail: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();

  const [serviceData, setServiceData] = useState<ServiceDetailData | null>(null);

  // 2-Phase Flow State: 1 = Diagnostic Questionnaire, 2 = Scope Selection & Contact Form
  const [activePhase, setActivePhase] = useState<1 | 2>(1);

  // Phase 1 Questionnaire State
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});

  // Phase 2 Package Selection State
  const [selectedPlanTier, setSelectedPlanTier] = useState<'basic' | 'standard' | 'premium'>('standard');

  useEffect(() => {
    if (serviceId) {
      const data = getServiceDetailData(serviceId);
      setServiceData(data);
      setActivePhase(1);
      setCurrentStep(0);
      setAnswers({});
      window.scrollTo(0, 0);
    }
  }, [serviceId]);

  if (!serviceData) {
    return (
      <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress color="primary" />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading Service Portal...</Typography>
      </Container>
    );
  }

  // Build FAQ schema from serviceData.faqs
  const faqSchema = serviceData.faqs && serviceData.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": serviceData.faqs.map((faq: any) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const questions = serviceData.questionnaire;
  const currentQuestion = questions[currentStep];

  const handleOptionSelect = (value: string) => {
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleNextQuestion = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setActivePhase(2); // Proceed to Pricing & Lead Form
    }
  };

  const handleBackQuestion = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', pb: 10 }}>
      <SEOHead
        title={`${serviceData.title} | Fingard Partners`}
        description={serviceData.tagline || `Expert ${serviceData.title} services from Fingard Partners. Get professional CA support for your business compliance needs.`}
        keywords={`${serviceData.title}, ${serviceData.category}, Fingard Partners, CA services India`}
        canonicalUrl={`/service/${serviceId}`}
      />
      {faqSchema && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </Helmet>
      )}
      {/* HERO BANNER SECTION */}
      <Box sx={{ bgcolor: '#123D6B', color: '#FFFFFF', pt: 5, pb: 7 }}>
        <Container maxWidth="lg">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />}
            sx={{ mb: 3, fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)' }}
          >
            <MuiLink component={RouterLink} to="/" color="inherit" underline="hover">
              Home
            </MuiLink>
            <MuiLink component={RouterLink} to="/services" color="inherit" underline="hover">
              Services
            </MuiLink>
            <Typography color="#C59B27" fontWeight="bold">
              {serviceData.title}
            </Typography>
          </Breadcrumbs>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label={serviceData.categoryTitle}
                sx={{ bgcolor: 'rgba(197, 155, 39, 0.2)', color: '#FFD700', fontWeight: 'bold', mb: 2 }}
              />
              <Typography variant="h3" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, lineHeight: 1.2 }}>
                {serviceData.title}
              </Typography>
              <Typography variant="h6" sx={{ color: '#CBD5E1', mb: 3, fontWeight: 400, maxWidth: 650 }}>
                {serviceData.tagline}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  icon={<AccessTime sx={{ color: '#FFD700 !important' }} />}
                  label={`Turnaround: ${serviceData.timeline}`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#FFFFFF', py: 2, px: 1, fontWeight: 'bold' }}
                />
                <Chip
                  icon={<VerifiedUser sx={{ color: '#FFD700 !important' }} />}
                  label="100% Paperless CA Process"
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#FFFFFF', py: 2, px: 1, fontWeight: 'bold' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper elevation={4} sx={{ p: 3.5, borderRadius: 4, bgcolor: '#FFFFFF', color: '#0F172A', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block">
                  SERVICE DIAGNOSTIC WIZARD
                </Typography>
                <Typography variant="h5" fontWeight={800} color="#123D6B" sx={{ my: 1 }}>
                  Complete Quiz to Unlock Pricing
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Answers tailor your compliance package & statutory tariff options.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, my: 1 }}>
                  <Chip
                    label={`Phase ${activePhase} of 3`}
                    color="secondary"
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* STEP FLOW INDICATOR TABS */}
      <Container maxWidth="md" sx={{ mt: -4, mb: 6, position: 'relative', zIndex: 10 }}>
        <Paper elevation={3} sx={{ p: 2, borderRadius: 3, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
          <Grid container spacing={1} textAlign="center">
            <Grid item xs={4}>
              <Box sx={{ py: 1, borderBottom: activePhase === 1 ? '3px solid #123D6B' : 'none' }}>
                <Typography variant="subtitle2" fontWeight={activePhase === 1 ? 800 : 500} color={activePhase === 1 ? '#123D6B' : 'text.secondary'}>
                  1. Questionnaire
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ py: 1, borderBottom: activePhase === 2 ? '3px solid #123D6B' : 'none' }}>
                <Typography variant="subtitle2" fontWeight={activePhase === 2 ? 800 : 500} color={activePhase === 2 ? '#123D6B' : 'text.secondary'}>
                  2. Pricing & Booking
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ py: 1, borderBottom: activePhase >= 3 ? '3px solid #10B981' : 'none' }}>
                <Typography variant="subtitle2" fontWeight={activePhase >= 3 ? 800 : 500} color={activePhase >= 3 ? '#10B981' : 'text.secondary'}>
                  3. Upload Documents
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Container maxWidth="lg">
        {/* MAIN INTERACTIVE FLOW SECTION */}
        <Container maxWidth="md" sx={{ mb: 8 }}>

          {/* ======================================================== */}
          {/* PHASE 1: DIAGNOSTIC QUESTIONNAIRE                        */}
          {/* ======================================================== */}
          {activePhase === 1 && (
            <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: '#FFFFFF' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                  Diagnostic Question {currentStep + 1} of {questions.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(((currentStep + 1) / questions.length) * 100)}% Progress
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight={800} color="#123D6B" sx={{ mb: 1 }}>
                {currentQuestion.question}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                {currentQuestion.description}
              </Typography>

              <Grid container spacing={2}>
                {currentQuestion.options.map((opt, i) => {
                  const isSelected = answers[currentQuestion.id] === opt.value;
                  return (
                    <Grid item xs={12} key={i}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 3,
                          borderColor: isSelected ? '#123D6B' : '#E2E8F0',
                          borderWidth: isSelected ? 2 : 1,
                          bgcolor: isSelected ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: '#C59B27', transform: 'translateY(-2px)' }
                        }}
                      >
                        <CardActionArea onClick={() => handleOptionSelect(opt.value)} sx={{ p: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6" fontWeight="bold" color="#0F172A" sx={{ fontSize: '1.05rem' }}>
                                {opt.label}
                              </Typography>
                              {opt.subtext && (
                                <Typography variant="body2" color="text.secondary">
                                  {opt.subtext}
                                </Typography>
                              )}
                            </Box>
                            {isSelected && <CheckCircle color="primary" />}
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  disabled={currentStep === 0}
                  onClick={handleBackQuestion}
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  disabled={!answers[currentQuestion.id]}
                  onClick={handleNextQuestion}
                  endIcon={<ArrowForward />}
                  sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, bgcolor: '#123D6B', px: 4, '&:hover': { bgcolor: '#C59B27' } }}
                >
                  {currentStep === questions.length - 1 ? 'Proceed to Pricing & Plans →' : 'Next Question'}
                </Button>
              </Box>
            </Paper>
          )}

          {/* ======================================================== */}
          {/* PHASE 2: PACKAGE SCOPE SELECTION & CONTACT FORM         */}
          {/* ======================================================== */}
          {activePhase === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Package Tier Scope Selector (Zero Public Prices) */}
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Chip label="Step 2: Choose Service Scope" color="primary" size="small" sx={{ mb: 1, fontWeight: 'bold', bgcolor: '#123D6B' }} />
                  <Typography variant="h5" fontWeight={800} color="#123D6B" gutterBottom>
                    Select Engagement Scope
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select your preferred package deliverables for <strong>{serviceData.title}</strong>. Our advisors will prepare a transparent quote based on your selection.
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {serviceData.pricingPlans.map((plan) => {
                    const isSel = selectedPlanTier === plan.tier;
                    return (
                      <Grid item xs={12} sm={4} key={plan.tier}>
                        <Card
                          variant="outlined"
                          onClick={() => setSelectedPlanTier(plan.tier)}
                          sx={{
                            p: 2.5,
                            cursor: 'pointer',
                            borderRadius: 3,
                            borderColor: isSel ? '#123D6B' : '#E2E8F0',
                            borderWidth: isSel ? 2 : 1,
                            bgcolor: isSel ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                            textAlign: 'center',
                            position: 'relative',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {plan.popular && (
                            <Chip label="Most Popular" color="secondary" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold' }} />
                          )}
                          <Typography variant="subtitle2" fontWeight="bold" color="#64748B" sx={{ textTransform: 'uppercase', mt: 1 }}>
                            {plan.name}
                          </Typography>
                          <Box sx={{ my: 1.5 }}>
                            <Chip label="Custom CA Quote" size="small" sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700 }} />
                          </Box>
                          <Divider sx={{ my: 1.5 }} />
                          {plan.features.map((feat, idx) => (
                            <Typography key={idx} variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5, textAlign: 'left' }}>
                              ✓ {feat}
                            </Typography>
                          ))}
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>

              {/* ServiceContactForm Component */}
              <ServiceContactForm
                serviceCategory={serviceData.category}
                selectedService={serviceData.title}
                selectedPackageTier={selectedPlanTier}
                diagnosticAnswers={answers}
                onBackToQuiz={() => setActivePhase(1)}
              />
            </Box>
          )}

        </Container>

        {/* OVERVIEW, PROCESS & FAQS ACCORDION SECTION */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mb: 4 }}>
              <Typography variant="h5" fontWeight={800} color="#123D6B" gutterBottom>
                Service Overview
              </Typography>
              <Typography variant="body1" color="#334155" sx={{ lineHeight: 1.8, fontSize: '1.05rem' }}>
                {serviceData.overview}
              </Typography>
            </Paper>

            {/* FILING PROCESS TIMELINE */}
            <Typography variant="h5" fontWeight={800} color="#123D6B" sx={{ mb: 3 }}>
              Fingard Standard Execution Process
            </Typography>

            <Grid container spacing={2}>
              {serviceData.processSteps.map((stepItem) => (
                <Grid item xs={12} sm={6} key={stepItem.step}>
                  <Card variant="outlined" sx={{ height: '100%', p: 2.5, borderRadius: 3, borderColor: '#E2E8F0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#123D6B', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                        {stepItem.step}
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="#123D6B">
                        {stepItem.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {stepItem.desc}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* REQUIRED DOCUMENTS SUMMARY */}
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '2px solid #123D6B', bgcolor: '#FFFFFF' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <AssignmentOutlined sx={{ color: '#123D6B', fontSize: 28 }} />
                <Typography variant="h5" fontWeight={800} color="#123D6B">
                  Document Checklist
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Required for official portal submission:
              </Typography>
              <Divider sx={{ mb: 2.5 }} />

              {serviceData.documentsRequired.map((doc, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                  <CheckCircle sx={{ color: '#10B981', fontSize: 20, mt: 0.2 }} />
                  <Typography variant="body2" color="#0F172A" fontWeight={500}>
                    {doc}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>

        {/* FAQS SECTION */}
        {serviceData.faqs && serviceData.faqs.length > 0 && (
          <Box sx={{ mb: 8 }}>
            <Box textAlign="center" sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight={800} color="#123D6B" gutterBottom>
                Frequently Asked Questions
              </Typography>
            </Box>
            <Container maxWidth="md">
              {serviceData.faqs.map((faq, idx) => (
                <Accordion key={idx} sx={{ borderRadius: 2, mb: 1.5, border: '1px solid #E2E8F0', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight="bold" color="#123D6B">
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Container>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ServiceDetail;
